import { useMemo } from 'react'
import { Aurora, AuroraAssetGroup } from '../types'
import {
  AuroraCirculationPumpMode,
  AuroraCirculationPumpStatus,
  AuroraHeatPumpStatus
} from '../enums'
import { AuroraAwhpActionNack } from '../errors'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { createErrorNotification } from '../redux/slices/notifications'

export const useAuroraStack = (auroraAssetGroup: AuroraAssetGroup) => {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const auroras = auroraAssetGroup.assets
    .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
    .map(({ aurora, ...asset }) => ({ ...aurora, asset }) as Aurora)

  const master = useMemo(() => {
    if (auroraAssetGroup.customData?.masterId === undefined) {
      return null
    }

    return auroras.find((aurora) => aurora.id === auroraAssetGroup.customData?.masterId) ?? null
  }, [auroras, auroraAssetGroup?.customData?.masterId])

  const heatPumpStatus = useMemo(
    () =>
      auroras.every((aurora) => aurora.isHeatPumpOn)
        ? AuroraHeatPumpStatus.ON
        : AuroraHeatPumpStatus.OFF,
    [auroras]
  )

  const targetTemperature = useMemo(() => {
    if (master !== null) {
      return master.targetTemperature1
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.max(...auroras.map((aurora) => aurora.targetTemperature1))
  }, [master, auroras])

  const circulationPumpStatus = useMemo(
    () =>
      auroras.every((aurora) => aurora.isCirculationPumpOn)
        ? AuroraCirculationPumpStatus.ON
        : AuroraCirculationPumpStatus.OFF,
    [auroras]
  )

  const circulationPumpMode = useMemo(() => {
    if (master !== null) {
      return master.circulationPumpMode
    }

    const everyIsAuto = auroras.every(
      (aurora) => aurora.circulationPumpMode === AuroraCirculationPumpMode.AUTO
    )

    if (everyIsAuto) {
      return AuroraCirculationPumpMode.AUTO
    }

    const everyIsMax = auroras.every(
      (aurora) => aurora.circulationPumpMode === AuroraCirculationPumpMode.MAX
    )

    if (everyIsMax) {
      return AuroraCirculationPumpMode.MAX
    }

    const everyIsMin = auroras.every(
      (aurora) => aurora.circulationPumpMode === AuroraCirculationPumpMode.MIN
    )

    if (everyIsMin) {
      return AuroraCirculationPumpMode.MIN
    }

    return AuroraCirculationPumpMode.AUTO
  }, [master])

  const minTemperatureLimit = useMemo(() => {
    if (master !== null) {
      return master.minTemperatureLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.max(...auroras.map((aurora) => aurora.minTemperatureLimit))
  }, [master, auroras])

  const minPressureLimit = useMemo(() => {
    if (master !== null) {
      return master.minPressureLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.max(...auroras.map((aurora) => aurora.minPressureLimit))
  }, [master, auroras])

  const minFlowLimit = useMemo(() => {
    if (master !== null) {
      return master.minFlowLimit
    }

    if (auroras.length === 0) {
      return null
    }

    return Math.max(...auroras.map((aurora) => aurora.minFlowLimit))
  }, [master, auroras])

  const handleAuroraAwhpActionError = (error: unknown, defaultMessage: string) => {
    if (error instanceof AuroraAwhpActionNack) {
      handleAuroraAwhpActionNack(error, defaultMessage)
    } else {
      dispatch(createErrorNotification(defaultMessage))
    }

    throw error
  }

  const handleAuroraAwhpActionNack = (error: AuroraAwhpActionNack, defaultMessage: string) => {
    switch (error.message) {
      case 'PIN_MISSING':
        dispatch(createErrorNotification(t('pinMissing')))
        break
      case 'PIN_REJECTED':
        dispatch(createErrorNotification(t('pinRejected')))
        break
      default:
        dispatch(createErrorNotification(defaultMessage))
        break
    }
  }

  return {
    auroras,
    master,
    heatPumpStatus,
    targetTemperature,
    circulationPumpStatus,
    circulationPumpMode,
    minTemperatureLimit,
    minPressureLimit,
    minFlowLimit,
    handleAuroraAwhpActionError
  }
}
